import { useMemo, useCallback } from 'react'
import { type IndexSymbol } from '../config/constants'
import { useMarketStore } from '../services/store'
import { useSettingsStore } from '../stores/settingsStore'

/**
 * Extract spotPrice from various snapshot shapes:
 * - D1/Worker compressed data: snap.spotPrice
 * - CSV rowsToSnapshots: snap.indexData[symbol].lastPrice
 * - Upstox raw: snap.index_ltp, snap.data?.ltp
 */
function extractSpotPrice(snap: any, symbol: string): number {
    if (!snap) return 0
    // Direct spotPrice (from transformed snapshots or worker blobs)
    if (snap.spotPrice && snap.spotPrice > 0) return snap.spotPrice
    // D1/Turso field
    if (snap.index_ltp && snap.index_ltp > 0) return snap.index_ltp
    if (snap.indexLtp && snap.indexLtp > 0) return snap.indexLtp
    // CSV rowsToSnapshots stores in indexData[symbol].lastPrice
    if (snap.indexData) {
        const idxData = snap.indexData[symbol] || snap.indexData[symbol?.toUpperCase()]
        if (idxData?.lastPrice && idxData.lastPrice > 0) return idxData.lastPrice
        // Fallback: first available key in indexData
        const keys = Object.keys(snap.indexData)
        for (const k of keys) {
            const d = snap.indexData[k]
            if (d?.lastPrice && d.lastPrice > 0) return d.lastPrice
        }
    }
    // Upstox raw data paths
    if (snap.data?.ltp && snap.data.ltp > 0) return snap.data.ltp
    return 0
}

const EMPTY_HISTORY_ARRAY: any[] = Object.freeze([]) as unknown as any[]

export function useAnalysisHistory({
    symbol,
    expiry,
    range,
    mode,
    baseTimePattern,
    viewType,
}: {
    symbol: IndexSymbol
    expiry: string
    range: number
    mode: 'OI' | 'VOLUME'
    baseTimePattern?: string // For crossover/variance reference
    viewType?: string
}) {
    // 1. Reactive Subscription to Store
    const inspectionSource = useSettingsStore((state) => state.inspectionSource)

    const intradayHistory = useMarketStore(useCallback((state) => (symbol ? state.intradayHistory[symbol] || EMPTY_HISTORY_ARRAY : EMPTY_HISTORY_ARRAY), [symbol]))
    const historicalHistory = useMarketStore(useCallback((state) => (symbol ? state.historicalHistory[symbol] || EMPTY_HISTORY_ARRAY : EMPTY_HISTORY_ARRAY), [symbol]))
    const rangeHistory = useMarketStore(useCallback((state) => (symbol ? state.rangeHistory[symbol] || EMPTY_HISTORY_ARRAY : EMPTY_HISTORY_ARRAY), [symbol]))

    const isLive = useSettingsStore((state) => state.isLive)
    const simulationTime = useSettingsStore((state) => state.simulationTime)

    // 🛡️ IRONCLAD: Short-circuit fullHistory selection — avoid creating new arrays when source is empty
    const fullHistory = useMemo(() => {
        if (inspectionSource === 'CurrentIntraday') return intradayHistory || EMPTY_HISTORY_ARRAY
        if (inspectionSource === 'HistoricalSnapshot') return historicalHistory || EMPTY_HISTORY_ARRAY
        if (inspectionSource === 'RangeIntraday' || inspectionSource === 'RangeEOD') return rangeHistory || EMPTY_HISTORY_ARRAY
        return EMPTY_HISTORY_ARRAY
    }, [inspectionSource, intradayHistory, historicalHistory, rangeHistory])

    // 🛡️ IRONCLAD: Skip expensive spread+filter when history is empty
    const truncatedHistory = useMemo(() => {
        if (!fullHistory || fullHistory.length === 0) return EMPTY_HISTORY_ARRAY

        // If we are not live and a simulationTime is set, truncate the history
        // so that the Intraday Tables properly update when the user scrubs the global Time Slicer
        if (!isLive && simulationTime) {
            const [h, m] = simulationTime.split(':').map(Number)
            const targetMin = (h ?? 0) * 60 + (m ?? 0)
            
            return fullHistory.filter((snap: any) => {
                if (snap._parsedMin !== undefined) {
                    return snap._parsedMin <= targetMin
                }
                
                let snapMin = 0
                if (snap.timestamp) {
                    const date = new Date(snap.timestamp)
                    // If timestamp is epoch ms, get IST time
                    // But we just need local hours/minutes if it's already local, or we can use the string once and cache it.
                }
                
                const tsStr = String(snap.timestampIST || snap.timestamp || '')
                let timePart = ''
                if (tsStr.includes('T')) timePart = tsStr.split('T')[1] || ''
                else if (tsStr.includes(' ')) timePart = tsStr.split(' ')[1] || ''
                else {
                    snap._parsedMin = -1
                    return true
                }
                
                timePart = timePart.replace(/[+Z].*$/, '')
                if (!timePart) {
                    snap._parsedMin = -1
                    return true
                }
                
                const parts = timePart.split(':')
                snapMin = parseInt(parts[0] || '0', 10) * 60 + parseInt(parts[1] || '0', 10)
                
                // Cache it so we never do this string parsing again for this snapshot
                Object.defineProperty(snap, '_parsedMin', { value: snapMin, enumerable: false, writable: true })
                
                return snapMin <= targetMin
            })
        }

        return fullHistory
    }, [fullHistory, isLive, simulationTime])

    // 2. Fetch the precomputed AnalysisHistory from the store
    const fullComputedArray = useMarketStore(useCallback((state) => (symbol ? state.analysisHistory?.[symbol] || EMPTY_HISTORY_ARRAY : EMPTY_HISTORY_ARRAY), [symbol]))

    // 🚀 PERF FIX: Synchronously slice the precomputed data to match simulation time
    const slicedData = useMemo(() => {
        if (!fullComputedArray || fullComputedArray.length === 0) return EMPTY_HISTORY_ARRAY;
        if (!truncatedHistory || truncatedHistory.length === 0) return EMPTY_HISTORY_ARRAY;
        
        const targetTimestamp = truncatedHistory[truncatedHistory.length - 1].timestamp;
        
        // If we haven't truncated anything, just return the full array to save filtering
        if (truncatedHistory.length === fullComputedArray.length && 
            targetTimestamp === fullComputedArray[fullComputedArray.length - 1]?.timestamp) {
            return fullComputedArray;
        }
        
        return fullComputedArray.filter((row: any) => (row.timestamp || 0) <= targetTimestamp);
    }, [fullComputedArray, truncatedHistory]);

    return {
        history: fullHistory,
        data: slicedData,
        isLoading: false,
        isFetching: false,
    }
}
